// pipeline-counts.ts
//
// Agregadores e invariantes de contadores de pipeline_runs por pipeline.
// Extraido de MCPipelinesPage.tsx pra ficar testavel e garantir o invariante:
//
//    total = executing + pending + blocked + completed + failed + cancelled (+ unknown)
//
// "unknown" e um fallback explicito pra status novos no backend que ainda nao
// foram mapeados na UI — em vez de fazer o card "sumir" ou exibir total
// inconsistente, o desconhecido vira a sua propria coluna ("outros").

import type { PipelineRunSummary } from './api-pipelines';

export interface RunCounts {
  executing: number;
  pending: number;
  blocked: number;
  completed: number;
  failed: number;
  cancelled: number;
  /** Status fora do enum conhecido (defesa contra desync UI x backend). */
  unknown: number;
  total: number;
  /** Tasks ainda nao terminais nos runs efetivamente em execucao. */
  activeTasks: number;
}

export function emptyCounts(): RunCounts {
  return {
    executing: 0, pending: 0, blocked: 0, completed: 0, failed: 0,
    cancelled: 0, unknown: 0, total: 0, activeTasks: 0,
  };
}

/**
 * Status considerados "rodando agora" — tarefas internas estao consumindo CPU.
 * pending/blocked NAO entram aqui (sao parados, nao em curso).
 */
export const RUNNING_RUN_STATUSES = new Set(['executing', 'decomposing']);

/**
 * Status TERMINAIS — runs onde nada mais pode rodar. NAO contam pra activeTasks.
 *
 * Tudo que nao esta aqui (executing, decomposing, pending, blocked, unknown)
 * e considerado nao-terminal e PODE ter tasks ativas (P1.13 F3).
 */
export const TERMINAL_RUN_STATUSES = new Set(['completed', 'failed', 'cancelled']);

/**
 * Agrega contadores por pipeline. Cada run cai em EXATAMENTE 1 categoria; o
 * total bate com a soma das categorias (incluindo unknown).
 */
export function aggregateRuns(runs: PipelineRunSummary[]): Map<string, RunCounts> {
  const out = new Map<string, RunCounts>();
  for (const r of runs) {
    const cur = out.get(r.pipeline_id) ?? emptyCounts();
    cur.total += 1;
    switch (r.status) {
      case 'executing':
      case 'decomposing':
        cur.executing += 1;
        break;
      case 'pending':
        cur.pending += 1;
        break;
      case 'blocked':
        cur.blocked += 1;
        break;
      case 'completed':
        cur.completed += 1;
        break;
      case 'failed':
        cur.failed += 1;
        break;
      case 'cancelled':
        cur.cancelled += 1;
        break;
      default:
        cur.unknown += 1;
        break;
    }
    // P1.13 F3: contar tasks ativas em TODOS os runs nao-terminais.
    //
    // Bug reportado em DEM-0852: pipeline_run em status='pending' tinha 11
    // tasks rodando (backend ainda nao migrou status pra 'executing' por
    // race entre @demand-spawner materializar tasks e listener atualizar
    // pipeline_runs.status — mas tasks JA estao rodando). Filtrar so por
    // executing/decomposing fazia "Tasks ativas: 0" mesmo com tasks reais.
    //
    // Backend B5 (P1.13) garante que tasks_planned/done/failed sao recontados
    // via advisory lock + RPC update_pipeline_run_counters, entao usar esses
    // contadores e' fiel ao estado real mesmo quando run.status atrasado.
    if (!TERMINAL_RUN_STATUSES.has(r.status)) {
      const remaining = Math.max(
        0,
        (r.tasks_planned ?? 0) - (r.tasks_done ?? 0) - (r.tasks_failed ?? 0),
      );
      cur.activeTasks += remaining;
    }
    out.set(r.pipeline_id, cur);
  }
  return out;
}

/**
 * Soma das categorias conhecidas (sem unknown). Usar pra detectar desync.
 */
export function sumKnownCategories(c: RunCounts): number {
  return c.executing + c.pending + c.blocked + c.completed + c.failed + c.cancelled;
}

/**
 * Quantidade que cairia em "outros" caso a UI seja chamada com runs em status
 * fora do enum mapeado. Sempre >= 0; igual a total - sum_known - unknown_ja_contado.
 *
 * Em caso de desync (ex: cliente puxou contagem agregada via outro endpoint
 * cujo total nao bate com as categorias somadas), retorna o gap absoluto.
 */
export function residualOthers(c: RunCounts): number {
  const known = sumKnownCategories(c);
  return Math.max(0, c.total - known - c.unknown);
}

/**
 * Verifica que total = soma de todas as categorias (incl. unknown + residual).
 * True quando os contadores estao internamente consistentes.
 */
export function isConsistent(c: RunCounts): boolean {
  return c.total === sumKnownCategories(c) + c.unknown + residualOthers(c);
}

/**
 * Runs ainda nao terminais — qualquer status que NAO seja completed/failed/cancelled.
 * Inclui executing, decomposing, pending, blocked e unknown (defesa anti-desync).
 *
 * Usar pra responder "quantos runs estao 'em curso' agora?" — a UI deve mostrar
 * pending/blocked aqui porque admin reportou (P1.11.E) que runs em status='pending'
 * sumiam do indicador "em execucao" (cenario real DEM-0819 / fd29ae3b).
 */
export function nonTerminalRuns(c: RunCounts): number {
  return c.executing + c.pending + c.blocked + c.unknown;
}

// ─── Display helpers (P1.13 F4) ──────────────────────────────────────────
//
// Padronizam label + tom visual de pipeline_runs.status em toda a UI.
// Antes cada lugar (PipelineDetailModal, PipelineRunDetailSheet, board chips)
// renderizava de jeito diferente: as vezes raw status string ("pending"),
// as vezes traduzido ("Pendente"), as vezes com cor, as vezes sem.
//
// F4 manda usar pipeline_runs.status DIRETO (sem inferir de tasks_done) e que
// o display seja consistente. Centralizar aqui garante isso.

/**
 * Label canonical PT-BR para pipeline_runs.status. Fallback retorna o proprio
 * status raw — defesa anti-desync (status novo no backend que UI nao mapeia
 * nao some, so aparece com nome tecnico).
 */
export function runStatusLabel(status: string): string {
  switch (status) {
    case 'executing': return 'Em execução';
    case 'decomposing': return 'Decompondo';
    case 'pending': return 'Pendente';
    case 'blocked': return 'Bloqueado';
    case 'completed': return 'Concluído';
    case 'failed': return 'Falhou';
    case 'cancelled': return 'Cancelado';
    default: return status;
  }
}

/**
 * Classe Tailwind de cor para pipeline_runs.status. Default neutro
 * (text-muted-foreground) pra status desconhecido.
 */
export function runStatusTone(status: string): string {
  switch (status) {
    case 'executing':
    case 'decomposing':
      return 'text-blue-600 dark:text-blue-400';
    case 'blocked':
      return 'text-yellow-700 dark:text-yellow-400';
    case 'completed':
      return 'text-green-600 dark:text-green-400';
    case 'failed':
      return 'text-red-600 dark:text-red-400';
    case 'pending':
    case 'cancelled':
    default:
      return 'text-muted-foreground';
  }
}
